import { Link } from 'react-router-dom'
import { EnvelopeIcon } from '@heroicons/react/24/solid'
import { ShopQuayWordmark } from '@/components/brand/ShopQuayWordmark'
import {
  LEGAL_COMPANY_ADDRESS,
  LEGAL_CONTACT_EMAIL,
  PLATFORM_FACEBOOK_URL,
  PLATFORM_WHATSAPP_URL,
} from '@/content/legal/company'
import { usePlatformBranding } from '@/hooks/usePlatformBranding'
import { useTranslation } from '@/i18n/useTranslation'

function FacebookIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
    </svg>
  )
}

function WhatsAppIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
  )
}

const EXPLORE_LINKS = [
  { to: '/pricing', labelKey: 'marketing.footer.pricing' as const },
  { to: '/onboarding', labelKey: 'marketing.footer.onboarding' as const },
  { to: '/help', labelKey: 'marketing.footer.help' as const },
  { to: '/partners', labelKey: 'marketing.footer.partners' as const },
  { to: '/signup', labelKey: 'marketing.footer.startFreeTrial' as const },
]

const LEGAL_LINKS = [
  { to: '/legal/terms', labelKey: 'marketing.footer.terms' as const },
  { to: '/legal/privacy', labelKey: 'marketing.footer.privacy' as const },
  { to: '/legal/dpa', labelKey: 'marketing.footer.dpa' as const },
]

const linkClass =
  'block text-base text-white/75 transition hover:text-white'

const contactClass =
  'inline-flex w-full items-center gap-3 rounded-xl border border-white/20 bg-white/10 px-4 py-3 text-base font-medium text-white transition hover:border-white/40 hover:bg-white hover:text-black focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white sm:w-auto'

const Footer = () => {
  const { t } = useTranslation('common')
  const { name } = usePlatformBranding()
  const year = new Date().getFullYear()
  const emailHref = `mailto:${LEGAL_CONTACT_EMAIL}`

  return (
    <footer className="bg-black text-white">
      <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-8 lg:py-16">
        <div className="grid grid-cols-1 gap-12 sm:grid-cols-2 lg:grid-cols-12 lg:gap-10">
          <div className="flex flex-col gap-4 sm:col-span-2 lg:col-span-5">
            <ShopQuayWordmark to="/" variant="inverse" heightClassName="h-12 sm:h-14" />
            <p className="max-w-sm text-base leading-relaxed text-white/70 sm:text-lg">
              {t('marketing.footer.tagline')}
            </p>
          </div>

          <div className="lg:col-span-2">
            <h3 className="mq-display text-sm font-semibold uppercase tracking-[0.12em] text-white/45">
              {t('marketing.footer.explore')}
            </h3>
            <nav className="mt-4 flex flex-col gap-3" aria-label={t('marketing.footer.explore')}>
              {EXPLORE_LINKS.map((item) => (
                <Link key={item.to} to={item.to} className={linkClass}>
                  {t(item.labelKey)}
                </Link>
              ))}
            </nav>
          </div>

          <div className="lg:col-span-2">
            <h3 className="mq-display text-sm font-semibold uppercase tracking-[0.12em] text-white/45">
              {t('marketing.footer.legal')}
            </h3>
            <nav className="mt-4 flex flex-col gap-3" aria-label={t('marketing.footer.legal')}>
              {LEGAL_LINKS.map((item) => (
                <Link key={item.to} to={item.to} className={linkClass}>
                  {t(item.labelKey)}
                </Link>
              ))}
            </nav>
          </div>

          <div className="sm:col-span-2 lg:col-span-3">
            <h3 className="mq-display text-sm font-semibold uppercase tracking-[0.12em] text-white/45">
              {t('marketing.footer.contact')}
            </h3>
            <p className="mt-4 text-sm leading-relaxed text-white/70">{LEGAL_COMPANY_ADDRESS}</p>
            <div className="mt-4 flex flex-col gap-3">
              <a
                href={PLATFORM_FACEBOOK_URL}
                target="_blank"
                rel="noopener noreferrer"
                className={contactClass}
                aria-label={t('marketing.footer.facebook')}
              >
                <FacebookIcon className="h-5 w-5 shrink-0" />
                <span>{t('marketing.footer.facebook')}</span>
              </a>
              <a
                href={PLATFORM_WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className={contactClass}
                aria-label={t('marketing.footer.whatsapp')}
              >
                <WhatsAppIcon className="h-5 w-5 shrink-0" />
                <span>{t('marketing.footer.whatsapp')}</span>
              </a>
              <a href={emailHref} className={contactClass} aria-label={t('marketing.footer.email')}>
                <EnvelopeIcon className="h-5 w-5 shrink-0" />
                <span className="truncate">{LEGAL_CONTACT_EMAIL}</span>
              </a>
            </div>
          </div>
        </div>

        <div className="mt-12 border-t border-white/20 pt-7">
          <p className="text-sm text-white/50 sm:text-base">
            &copy; {year} {name}. {t('marketing.footer.rights')}
          </p>
        </div>
      </div>
    </footer>
  )
}

export default Footer
